import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'
import { setMetaTags } from '@/utils/seo'

/**
 * Applies admin-managed DB SEO meta (via useSEO + the build-time snapshot) to the
 * info/listing pages that don't set their own meta. Landing pages (SEOHead), GEO
 * city pages (setMetaTags) and the homepage (its own useSEO) are intentionally NOT
 * listed here, so this never overrides a page that already manages its meta.
 */
const PATH_TO_SLUG: Record<string, string> = {
  '/about': 'about',
  '/contact': 'contact',
  '/network-coverage': 'network-coverage',
  '/roaming': 'roaming',
  '/voip': 'voip',
  '/free-sim': 'free-sim',
  '/special-offers': 'special-offers',
  '/reseller-program': 'reseller-program',
  '/faq': 'faq',
  '/sim-only': 'sim-only',
  '/broadband': 'broadband',
}

/**
 * Auth / account / transactional routes that must NOT be indexed. Matched by prefix
 * so param routes (/reset-password/:token, /order-confirmation/:id, /sign-contract/:token)
 * and whole authed areas (/account/*, /admin/*) are covered.
 */
const NOINDEX_PREFIXES = [
  '/login', '/register', '/verify', '/forgot-password', '/reset-password',
  '/first-time-login', '/complete-profile', '/cart', '/checkout',
  '/order-confirmation', '/unsubscribe', '/sign-contract', '/spin-wheel',
  '/account', '/admin',
  // World MultiNet zone pages are intentionally HIDDEN (not in nav/sitemap/prerender).
  // Keep them out of the index in case the URLs are ever hit directly.
  '/roaming/zone1', '/roaming/zone2', '/roaming/zone3',
  '/roaming/zone4', '/roaming/zone5', '/roaming/zone6',
]

/**
 * Legal / policy pages — indexable, but they previously emitted no meta. Give each a
 * title + description (canonical/hreflang are added automatically by setMetaTags).
 */
const LEGAL_META: Record<string, { title: string; description: string }> = {
  '/terms': { title: 'Terms & Conditions | Silux Mobile', description: 'Read the Silux Mobile terms and conditions for our SIM only, mobile broadband and hardware services.' },
  '/privacy': { title: 'Privacy Policy | Silux Mobile', description: 'How Silux Mobile collects, uses, stores and protects your personal data, and your rights under UK GDPR.' },
  '/free-sim-terms': { title: 'Free SIM Terms | Silux Mobile', description: 'The terms and conditions for the Silux Mobile free SIM card offer and 7-day free trial.' },
  '/acceptable-use': { title: 'Acceptable Use Policy | Silux Mobile', description: 'The Silux Mobile acceptable use policy governing fair use of our mobile and broadband network.' },
  '/repairs-replacements': { title: 'Repairs & Replacements | Silux Mobile', description: 'The Silux Mobile repairs and replacements policy for routers, devices and hardware.' },
  '/website-terms': { title: 'Website Terms of Use | Silux Mobile', description: 'The terms governing your use of the Silux Mobile website.' },
  '/complaints-code': { title: 'Complaints Code of Practice | Silux Mobile', description: 'How to make a complaint to Silux Mobile, what to expect, and our code of practice.' },
  '/cookie-policy': { title: 'Cookie Policy | Silux Mobile', description: 'How Silux Mobile uses cookies and similar technologies, and how to manage your preferences.' },
  '/early-termination': { title: 'Early Termination Charges | Silux Mobile', description: 'How Silux Mobile early termination charges work and how they are calculated.' },
}

function PageSEOApplier({ slug }: { slug: string }) {
  useSEO(slug)
  return null
}

function StaticMetaApplier({ title, description, robots }: { title?: string; description?: string; robots?: string }) {
  useEffect(() => {
    setMetaTags({ title, description, robots })
  }, [title, description, robots])
  return null
}

export default function RouteSEO() {
  const { pathname } = useLocation()

  const slug = PATH_TO_SLUG[pathname]
  if (slug) return <PageSEOApplier slug={slug} />

  if (NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return <StaticMetaApplier robots="noindex, nofollow" />
  }

  const legal = LEGAL_META[pathname]
  if (legal) return <StaticMetaApplier title={legal.title} description={legal.description} />

  return null
}
